import React from 'react';
import ResultsExperience from './results/ResultsExperience';

const MOCK_ASSESSMENT = {
  assessment_id: 'demo-001',
  user_id: 'demo-user-001',
  recommendations: [
    {
      group: 'Australia', priority: 100, fit_key: 'strong_fit', display_label: 'Strong Fit',
      tagline: 'Permanent Residency Track',
      summary: 'Points estimate above the minimum threshold. A skills assessment is the key next step. State nomination could add further points.',
      score_range: { confirmed_min: 70, probable_max: 85, max_possible: 130, label: 'Points (65 min)' },
      requirements: [
        { gate: 'skills_assessment', passed: null, note: 'Skills assessment required — not yet confirmed' },
        { gate: 'occupation_list', passed: true, note: 'Software Engineer is on the skilled occupation list' },
        { gate: 'english_competent', passed: true, note: 'IELTS 7.5 = Superior English (20 pts)' },
        { gate: 'age_under_45', passed: true, note: 'Age 29 = 30 points' },
      ],
      actions: ['Complete a skills assessment', 'Explore state nomination options'],
      conditions: ['Skills assessment pending'],
      members: [],
    },
    {
      group: 'New Zealand', priority: 75, fit_key: 'possible_fit', display_label: 'Possible Fit',
      tagline: 'Permanent Residency Track',
      summary: 'Competitive profile, but a New Zealand job offer is effectively required to reach the points threshold.',
      score_range: { confirmed_min: 4, probable_max: 6, max_possible: 10, label: 'Points (6 min)' },
      requirements: [
        { gate: 'skilled_employment', passed: null, note: 'Job offer in NZ required or current employment' },
        { gate: 'age_under_55', passed: true, note: 'Age 29 qualifies' },
      ],
      actions: ['Secure a job offer from a New Zealand employer'],
      conditions: ['Job offer required'],
      members: [],
    },
    {
      group: 'Canada', priority: 70, fit_key: 'possible_fit', display_label: 'Possible Fit',
      tagline: 'Permanent Residency Track',
      summary: 'Strong profile for skilled migration. Points score likely above recent cutoffs. Provincial nomination could add bonus points.',
      score_range: { confirmed_min: 468, probable_max: 502, max_possible: 600, label: 'Points Score (600 max)' },
      requirements: [
        { gate: 'language_minimum', passed: true, note: 'CLB 9+ achieved via IELTS 7.5' },
        { gate: 'education_credential', passed: true, note: "Master's degree recognized" },
        { gate: 'skilled_occupation', passed: true, note: 'Software Engineer qualifies as skilled' },
        { gate: 'work_experience', passed: true, note: '6 years qualifies for max points' },
      ],
      actions: ['Get your education credentials assessed', 'Consider provincial nomination options'],
      conditions: [],
      members: [],
    },
    {
      group: 'United Kingdom', priority: 60, fit_key: 'business_review', display_label: 'Business Review Recommended',
      tagline: 'Work & Residency Options',
      summary: 'Your profile shows potential for the UK, but UK routes depend on employer sponsorship and salary thresholds. Our consultants will review the best UK strategy for you.',
      score_range: null,
      requirements: [
        { gate: 'licensed_sponsor', passed: null, note: 'Employer sponsor required — not yet identified' },
        { gate: 'english_b1', passed: true, note: 'IELTS 7.5 exceeds the requirement' },
      ],
      actions: ['Discuss UK sponsorship strategy with an Axpats consultant'],
      conditions: ['Sponsor required'],
      members: [],
    },
    {
      group: 'Europe', priority: 45, fit_key: 'possible_fit', display_label: 'Possible Fit',
      tagline: 'Portugal · Germany · Estonia',
      summary: '3 European destinations match your profile — strongest option: Portugal. Expand to see each country.',
      score_range: null,
      requirements: [],
      actions: [],
      conditions: [],
      members: [
        { country: 'Portugal', fit_key: 'strong_fit', display_label: 'Strong Fit', summary: 'The remote work route is highly accessible. Income requirement easily met. Can lead to residency after 5 years.' },
        { country: 'Germany', fit_key: 'possible_fit', display_label: 'Possible Fit', summary: 'Qualification base is strong. German language skills would significantly improve your points-based score.' },
        { country: 'Estonia', fit_key: 'possible_fit', display_label: 'Possible Fit', summary: 'The remote work route is ideal for remote workers. Verify monthly income meets the threshold.' },
      ],
    },
  ],
  profile_summary: {
    age: { value: '29', confidence: 'confirmed' },
    education: { value: "Master's (Computer Science)", confidence: 'confirmed' },
    experience_years: { value: '6 years', confidence: 'confirmed' },
    language: { value: 'IELTS 7.5', confidence: 'confirmed' },
    nationality: { value: 'Indian', confidence: 'confirmed' },
    occupation: { value: 'Software Engineer', confidence: 'inferred' },
    salary_usd: { value: '$85,000', confidence: 'inferred' },
  },
  pr_countries: [
    {
      country: 'Canada',
      pathways: ['Express Entry (FSW)', 'PNP'], track_label: 'Permanent Residency Track',
      pr_fit: 'likely_fit',
      score_range: { confirmed_min: 468, probable_max: 502, max_possible: 600, label: 'CRS Score (600 max)' },
      hard_gates: [
        { gate: 'language_minimum', passed: true, note: 'CLB 9+ achieved via IELTS 7.5' },
        { gate: 'education_credential', passed: true, note: "Master's degree recognized" },
        { gate: 'skilled_occupation', passed: true, note: 'NOC 21232 — Software Engineer' },
        { gate: 'work_experience', passed: true, note: '6 years qualifies for max points' },
      ],
      next_actions: ['Get ECA done for Masters degree', 'Consider PNP for Ontario Tech stream'],
      notes: 'Strong profile for Express Entry. CRS score likely above recent draw cutoffs. Provincial Nominee Programs could add 600 bonus points.',
      conditions: [],
    },
    {
      country: 'Australia',
      pathways: ['Subclass 189', 'Subclass 190'], track_label: 'Permanent Residency Track',
      pr_fit: 'possible_fit',
      score_range: { confirmed_min: 70, probable_max: 85, max_possible: 130, label: 'Points (65 min)' },
      hard_gates: [
        { gate: 'skills_assessment', passed: null, note: 'ACS assessment required — not yet confirmed' },
        { gate: 'occupation_list', passed: true, note: 'Software Engineer on MLTSSL' },
        { gate: 'english_competent', passed: true, note: 'IELTS 7.5 = Superior English (20 pts)' },
        { gate: 'age_under_45', passed: true, note: 'Age 29 = 30 points' },
      ],
      next_actions: ['Apply for ACS skills assessment', 'Check state nomination options for 190'],
      notes: 'Points estimate above the 65-point threshold. Skills assessment is the key missing step. State nomination (190) could add 5 points.',
      conditions: ['Skills assessment pending'],
    },
    {
      country: 'Germany',
      pathways: ['Opportunity Card', 'EU Blue Card'], track_label: 'Permanent Residency Track',
      pr_fit: 'possible_fit',
      score_range: { confirmed_min: 8, probable_max: 12, max_possible: 21, label: 'Opportunity Card (6 min)' },
      hard_gates: [
        { gate: 'qualification_recognized', passed: true, note: "Master's in CS recognized in Germany" },
        { gate: 'language_german_a1', passed: null, note: 'German language level not confirmed' },
      ],
      next_actions: ['Start German A1 course for bonus points', 'Check anabin for degree recognition'],
      notes: 'Qualification base is strong. German language skills would significantly improve Opportunity Card score. EU Blue Card possible with job offer.',
      conditions: ['German language level unknown'],
    },
    {
      country: 'New Zealand',
      pathways: ['Skilled Migrant Category'], track_label: 'Permanent Residency Track',
      pr_fit: 'conditional_fit',
      score_range: { confirmed_min: 4, probable_max: 6, max_possible: 10, label: 'SMC Points (6 min)' },
      hard_gates: [
        { gate: 'skilled_employment', passed: null, note: 'Job offer in NZ required or current employment' },
        { gate: 'age_under_55', passed: true, note: 'Age 29 qualifies' },
      ],
      next_actions: ['Secure a job offer from NZ employer for guaranteed points'],
      notes: 'Competitive profile but a NZ job offer is effectively required to reach the 6-point threshold.',
      conditions: ['Job offer required'],
    },
  ],
  wp_countries: [
    {
      country: 'United Kingdom',
      pathways: ['Skilled Worker Visa', 'Global Talent'], track_label: 'Work & Residency Options',
      wp_fit: 'possible_fit',
      score_range: { confirmed_min: 50, probable_max: 70, max_possible: 70, label: 'Points (70 required)' },
      hard_gates: [
        { gate: 'licensed_sponsor', passed: null, note: 'Employer sponsor required — not yet identified' },
        { gate: 'english_b1', passed: true, note: 'IELTS 7.5 exceeds B1 requirement' },
        { gate: 'minimum_salary', passed: null, note: 'Salary must meet GBP 26,200 threshold' },
      ],
      next_actions: ['Apply to UK-licensed sponsor employers', 'Consider Global Talent for Tech Nation endorsement'],
      notes: 'Skilled Worker visa requires an employer sponsor. Global Talent route could work if you can demonstrate exceptional talent in digital technology.',
      conditions: ['Sponsor required'],
    },
    {
      country: 'Portugal',
      pathways: ['D8 Digital Nomad', 'D7 Passive Income'], track_label: 'Work & Residency Options',
      wp_fit: 'likely_fit',
      hard_gates: [
        { gate: 'remote_employment', passed: true, note: 'Remote work for non-Portuguese company' },
        { gate: 'income_minimum', passed: true, note: 'Income exceeds EUR 3,500/month requirement' },
      ],
      next_actions: ['Gather proof of remote employment', 'Arrange Portuguese health insurance'],
      notes: 'D8 Digital Nomad visa is highly accessible. Income requirement easily met. Can lead to residency after 5 years.',
      conditions: [],
    },
    {
      country: 'Estonia',
      pathways: ['Digital Nomad Visa'], track_label: 'Work & Residency Options',
      wp_fit: 'possible_fit',
      hard_gates: [
        { gate: 'remote_employment', passed: true, note: 'Working remotely for foreign employer' },
        { gate: 'income_eur_4500', passed: null, note: 'EUR 4,500/month gross income required — verify salary in EUR' },
      ],
      next_actions: ['Confirm monthly income meets EUR 4,500 threshold'],
      notes: 'Estonia DN visa ideal for digital nomads. E-residency available. No path to PR through this visa alone.',
      conditions: ['Income verification needed'],
    },
  ],
  conditional_opportunities: [
    {
      country: 'Greece',
      pathways: ['Golden Visa (Investment)'], track_label: 'Permanent Residency Track',
      pr_fit: 'conditional_fit',
      hard_gates: [
        { gate: 'investment_minimum', passed: null, note: 'EUR 250,000+ real estate investment required' },
      ],
      next_actions: ['Evaluate if investment route aligns with your financial goals'],
      notes: 'Investment-based residence. Requires EUR 250K+ property purchase. Attractive for those with capital.',
      conditions: ['Investment capital required'],
    },
  ],
  follow_up_questions: [
    { question: 'Do you have a skills assessment from ACS (Australia)?', impact_countries: ['Australia'], priority: 'high' },
    { question: 'Do you have any German language certification?', impact_countries: ['Germany'], priority: 'medium' },
    { question: 'Do you currently work remotely for a non-EU employer?', impact_countries: ['Portugal', 'Estonia', 'Croatia'], priority: 'high' },
    { question: 'Do you have a job offer from a UK-licensed sponsor?', impact_countries: ['United Kingdom'], priority: 'high' },
  ],
  next_best_actions: [
    'Get your education credentials assessed for Canada — this alone could unlock a points boost',
    'Take IELTS again aiming for 8.0+ in each band to maximize CLB 10 points across Canada, Australia, and UK',
    'Complete a skills assessment to unlock Australia skilled migration',
    'Explore Canadian provincial nomination — your Software Engineering background is in demand',
    'Start German A1/A2 to boost your Germany options',
    'Gather 6 months of payslips and remote work proof for the Portugal application',
  ],
};

const MOCK_USER = {
  full_name: 'Arjun Mehta',
  email: 'arjun.mehta@example.com',
  phone: '+91 98765 43210',
  nationality: 'Indian',
};

const ResultsDemo = () => {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <ResultsExperience assessment={MOCK_ASSESSMENT} userData={MOCK_USER} />
    </div>
  );
};

export default ResultsDemo;
